var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import debug from "debug";
import ky from "ky";
import { localStore } from "@auvious/utils";
import { AUVIOUS_VALID_URL_REGEX, ErrorCode, KEY_VERINT_LIVE, KEY_VERINT_LOG_MSG, KEY_VERINT_LOG_MEMBERS, WIDGET_COBROWSE_REQUEST_PREFIX, KEY_VERINT_LOG_ATTACH, } from "./models/enums";
import { codeError, timeout } from "./utils";
import { Restorable } from "./models/Restorable";
const log = debug("auvious:integrations");
export class VerintLiveChatService extends Restorable {
    constructor(config, handlers) {
        super();
        this.config = config;
        this.handlers = handlers;
        this.attachments = {};
        this.attachmentConstraints = {
            mimeType: ".jpeg,.jpg,.png,.gif",
            fileSize: 1024 * 1024 * 100,
            friendlyFileSize: "100 MiB",
        };
        this.doPolling = false;
        this.wasRecovered = false;
        this.agentTypedLastTime = 0;
        this.agentTypingTimeout = -1;
        this.agentHasEntered = false;
        /** track sent message progress (because verint has no ids for them) */
        this.pendingMessages = [];
        // properties used in the synchronous live chat
        this.messageHistory = [];
        if (config.customDomain) {
            this.customDomain = new RegExp(`^https://(.*${config.customDomain})(:\\d+)?/t/.*$`, "i");
        }
        const vtUrl = config.vtServerUrl.startsWith("http")
            ? config.vtServerUrl
            : `https://${config.vtServerUrl}`;
        this.verintUrl = new URL(vtUrl).origin;
        this.vtChatMode = config.vtChatMode || "CHAT_ONLY";
        this.tenantId = config.vtTenantId || "default";
        this.discoverServiceUrls().then(() => {
            var _a, _b;
            (_b = (_a = this.handlers) === null || _a === void 0 ? void 0 : _a.ready) === null || _b === void 0 ? void 0 : _b.call(_a);
        });
    }
    recoverSession() {
        const store = localStore.getItem(KEY_VERINT_LIVE);
        if (store) {
            this.session = JSON.parse(store);
            this.wasRecovered = true;
            // in chat_only we have to manually keep track of session data
            if (this.vtChatMode === "CHAT_ONLY") {
                try {
                    this.$members = JSON.parse(localStore.getItem(KEY_VERINT_LOG_MEMBERS));
                }
                catch (_a) {
                    this.$members[this.session.userLoginId] = {
                        displayName: this.session.userName,
                        id: this.session.userLoginId,
                        joinDate: new Date().toISOString(),
                        role: "CUSTOMER",
                    };
                }
                this.agentHasEntered = !!Object.values(this.$members).find((member) => member.role === "AGENT");
                try {
                    this.messageHistory = JSON.parse(localStore.getItem(KEY_VERINT_LOG_MSG));
                    this.messageHistory.forEach((m) => (m.timestamp = new Date(m.timestamp)));
                    this.attachments = JSON.parse(localStore.getItem(KEY_VERINT_LOG_ATTACH));
                }
                catch (_b) { }
            }
        }
        return !!this.session;
    }
    discoverServiceUrls() {
        return __awaiter(this, void 0, void 0, function* () {
            this.serviceLinks = yield ky
                .get(`${this.verintUrl}/live-chat-protocol-converter/${this.tenantId}/CONAV/chat/rest/api`)
                .json();
        });
    }
    /** exists mainly for proxies */
    adjustOrigin(url, origin) {
        const u = new URL(url);
        const nu = new URL(origin);
        u.protocol = nu.protocol;
        u.hostname = nu.hostname;
        u.port = nu.port;
        return u.toString();
    }
    get isAsyncChat() {
        return this.vtChatMode === "ASYNCHRONOUS_CHAT_ONLY";
    }
    setupArchiveOnRefresh() {
        if (!this.isAsyncChat) {
            window.addEventListener("beforeunload", () => {
                if (this.session) {
                    localStore.setItem(KEY_VERINT_LOG_MEMBERS, JSON.stringify(this.$members));
                    localStore.setItem(KEY_VERINT_LOG_ATTACH, JSON.stringify(this.attachments));
                    localStore.setItem(KEY_VERINT_LOG_MSG, JSON.stringify(this.messageHistory));
                }
            });
        }
    }
    initClient(options) {
        var _a, _b;
        var _c, _d;
        return __awaiter(this, void 0, void 0, function* () {
            const body = {
                chatLaunchMode: this.vtChatMode,
                customerEmail: options.email || "",
                customerFirstName: options.firstName,
                customerLastName: options.lastName,
                customerQuery: options.firstMessage || "",
                launchIdentifier: "VerintChat",
                launchCode: "",
                refererURL: location.href,
                customerName: "",
            };
            // user recovered session
            if (this.isAsyncChat && ((_a = this.session) === null || _a === void 0 ? void 0 : _a.userLoginId))
                body.userLoginId = this.session.userLoginId;
            this.session = yield ky
                .post(`${this.adjustOrigin(this.serviceLinks._links.client.href, this.verintUrl)}`, {
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(body),
            })
                .json();
            localStore.setItem(KEY_VERINT_LIVE, JSON.stringify(this.session));
            (_b = (_c = this.$members)[_d = this.session.userLoginId]) !== null && _b !== void 0 ? _b : (_c[_d] = {
                displayName: this.session.userName,
                id: this.session.userLoginId,
                joinDate: new Date().toISOString(),
                role: "CUSTOMER",
            });
        });
    }
    /** returns whether in queue */
    getQueueStatus() {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            // on refresh agent might had already entered
            if (this.agentHasEntered) {
                return false;
            }
            const url = this.queueStatus
                ? this.adjustOrigin(this.queueStatus._links.nextqueuedstatus.href, this.verintUrl)
                : `${this.verintUrl}/live-chat-protocol-converter/${this.tenantId}/CONAV/chat/rest/api/clients/${this.session.userId}/queuedstatus`;
            this.queueStatus = yield ky.get(url).json();
            return (!this.agentHasEntered && ((_a = this.queueStatus.message) === null || _a === void 0 ? void 0 : _a.startsWith("Thank")));
        });
    }
    getEvents() {
        return __awaiter(this, void 0, void 0, function* () {
            const req = yield ky
                .get(`${this.verintUrl}/live-chat-protocol-converter/${this.tenantId}/CONAV/chat/rest/api/clients/${this.session.userId}/events`)
                .json();
            this.handleEvents(req.events);
        });
    }
    handleEvents(events) {
        for (const eventStr of events) {
            try {
                const event = JSON.parse(eventStr);
                this.handleEvent(event);
            }
            catch (ex) {
                log(ex);
            }
        }
    }
    handleEvent(event) {
        var _a, _b, _c, _d, _e, _f, _g;
        switch (event.type) {
            case "AttachmentMessageReceived":
                {
                    const member = this.getMemberByName(event.userDisplayName);
                    let messageId;
                    let file;
                    if ((member === null || member === void 0 ? void 0 : member.role) === "CUSTOMER") {
                        // verint replaces some characters with underscores
                        messageId = event.fileName.slice(0, event.fileName.indexOf("."));
                        const msgIndex = this.pendingMessages.findIndex((msg) => msg.id.slice(0, msg.id.indexOf(".")) === messageId);
                        if (msgIndex >= 0) {
                            file = Object.assign(Object.assign({}, this.pendingMessages.splice(msgIndex, 1)[0]), { status: "ack" });
                        }
                    }
                    else {
                        messageId = event.identifier;
                        file = {
                            id: messageId,
                            type: "file",
                            file: {
                                id: messageId,
                                name: event.fileName,
                                size: NaN,
                            },
                            isLocal: false,
                            timestamp: new Date(),
                            status: "ack",
                            senderId: member.id,
                        };
                    }
                    if (file) {
                        this.attachments[messageId] = Object.assign(Object.assign({}, event), { identifier: messageId });
                        (_a = this.handlers) === null || _a === void 0 ? void 0 : _a.messageReceived(this.archiveMessage(file));
                    }
                }
                break;
            case "ClientConfigurationReceived":
                break;
            case "Dropped":
            case "DroppedAsync":
                (_b = this.handlers) === null || _b === void 0 ? void 0 : _b.ended();
                this.destroy();
                break;
            case "History":
                event.messageEvents.forEach((msg) => {
                    if (this.isMessageLocal(msg))
                        this.onOutgoingMessage(msg, true);
                    else
                        this.onIncomingMessage(msg, true);
                });
                break;
            case "LoggedOut":
                (_c = this.handlers) === null || _c === void 0 ? void 0 : _c.agentLeft();
                break;
            case "MessageReceived":
                if (this.isMessageLocal(event))
                    this.onOutgoingMessage(event);
                else
                    this.onIncomingMessage(event);
                break;
            case "Parked":
                // agent parks async chat, todo: find out how to handle this
                break;
            case "UserEntered":
                if (event.userType === "AGENT") {
                    this.agentHasEntered = true;
                    this.$members[event.userId] = {
                        displayName: event.userDisplayName,
                        id: event.userId,
                        joinDate: new Date().toISOString(),
                        role: "AGENT",
                    };
                    (_d = this.handlers) === null || _d === void 0 ? void 0 : _d.agentJoined(this.$members[event.userId]);
                }
                break;
            case "UserExited":
                {
                    const member = this.getMemberByName(event.userDisplayName);
                    if (!member) {
                        break;
                    }
                    else if (member.role === "AGENT") {
                        (_e = this.handlers) === null || _e === void 0 ? void 0 : _e.agentLeft();
                        this.removeMemberByName(member.displayName);
                    }
                    else if (member.role === "CUSTOMER") {
                        (_f = this.handlers) === null || _f === void 0 ? void 0 : _f.customerLeft();
                        this.removeMemberByName(member.displayName);
                    }
                }
                break;
            case "UserTyping": {
                const member = this.getMemberByName(event.userDisplayName);
                if ((member === null || member === void 0 ? void 0 : member.role) === "AGENT") {
                    this.agentTypedLastTime = Date.now();
                    if (this.agentTypingTimeout === -1)
                        (_g = this.handlers) === null || _g === void 0 ? void 0 : _g.agentTypingStarted();
                    else
                        clearTimeout(this.agentTypingTimeout);
                    this.agentTypingTimeout = window.setTimeout(() => {
                        var _a;
                        this.agentTypingTimeout = -1;
                        if (Date.now() - this.agentTypedLastTime > 2000) {
                            (_a = this.handlers) === null || _a === void 0 ? void 0 : _a.agentTypingStopped();
                        }
                    }, 2000);
                }
                break;
            }
            default:
                log("ignored event", event);
                break;
        }
    }
    getMemberByName(displayName) {
        for (const id in this.$members) {
            if (this.$members[id].displayName === displayName) {
                return this.$members[id];
            }
        }
        return null;
    }
    removeMemberByName(displayName) {
        for (const id in this.$members) {
            if (this.$members[id].displayName === displayName) {
                delete this.$members[id];
                break;
            }
        }
    }
    reconnect(contact, conversationType) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q;
        return __awaiter(this, void 0, void 0, function* () {
            (_b = (_a = this.handlers) === null || _a === void 0 ? void 0 : _a.starting) === null || _b === void 0 ? void 0 : _b.call(_a);
            (_d = (_c = this.handlers) === null || _c === void 0 ? void 0 : _c.connecting) === null || _d === void 0 ? void 0 : _d.call(_c);
            if (this.recoverSession()) {
                this.setupArchiveOnRefresh();
                if (this.isAsyncChat)
                    yield this.initClient({
                        email: contact.email,
                        firstMessage: contact.subject,
                        firstName: contact.firstName,
                        lastName: contact.lastName,
                    });
                this.sessionContact = contact;
                this.sessionConversationType = conversationType;
                const members = Object.assign({}, this.$members);
                // there is no delay in this method, but needed by the "consumer"
                yield new Promise(requestAnimationFrame);
                (_f = (_e = this.handlers) === null || _e === void 0 ? void 0 : _e.started) === null || _f === void 0 ? void 0 : _f.call(_e, {
                    id: this.session.userId,
                    jwt: null,
                    memberId: this.session.userLoginId,
                    uri: null,
                });
                (_h = (_g = this.handlers) === null || _g === void 0 ? void 0 : _g.reconnected) === null || _h === void 0 ? void 0 : _h.call(_g);
                for (const mid in members) {
                    (_j = this.handlers) === null || _j === void 0 ? void 0 : _j.memberSet(members[mid]);
                }
                if (!this.agentHasEntered)
                    yield ((_l = (_k = this.handlers) === null || _k === void 0 ? void 0 : _k.agentNotified) === null || _l === void 0 ? void 0 : _l.call(_k));
                else
                    (_o = (_m = this.handlers) === null || _m === void 0 ? void 0 : _m.agentJoined) === null || _o === void 0 ? void 0 : _o.call(_m, Object.values(members).find((m) => m.role === "AGENT"));
                for (const msg of this.messageHistory) {
                    (_q = (_p = this.handlers) === null || _p === void 0 ? void 0 : _p.messageReceived) === null || _q === void 0 ? void 0 : _q.call(_p, msg);
                }
                this.pollTask();
            }
        });
    }
    isMessageLocal(message) {
        if ("userType" in message) {
            return message.userType === "GUEST";
        }
        else {
            const user = this.$members[message.userId];
            return (user === null || user === void 0 ? void 0 : user.role) === "CUSTOMER";
        }
    }
    onOutgoingMessage(message, isHistory = false) {
        var _a;
        const msgIndex = this.pendingMessages.findIndex((msg) => msg.text === message.text);
        if (msgIndex >= 0) {
            const [msg] = this.pendingMessages.splice(msgIndex, 1);
            (_a = this.handlers) === null || _a === void 0 ? void 0 : _a.messageReceived(this.archiveMessage(Object.assign(Object.assign({}, msg), { status: "ack" })));
        }
    }
    onIncomingMessage(message, isHistory = false) {
        var _a, _b, _c, _d, _e, _f, _g;
        try {
            if (message.userType === "SYSTEM") {
                // ignore system messages
                return;
            }
            const user = "userId" in message
                ? this.$members[message.userId]
                : this.getMemberByName(message.userDisplayName);
            // unknown user
            if (!user)
                return;
            const id = Date.now() + "";
            const isLocal = false;
            const timestamp = message.timestamp
                ? new Date(message.timestamp)
                : new Date();
            const text = message.text;
            const status = "ack";
            const senderId = user.id;
            if (AUVIOUS_VALID_URL_REGEX.test(text) || ((_a = this.customDomain) === null || _a === void 0 ? void 0 : _a.test(text))) {
                // do not open a past call
                if (!isHistory) {
                    log("Verint: room created", text);
                    (_c = (_b = this.handlers).roomCreated) === null || _c === void 0 ? void 0 : _c.call(_b, text);
                }
            }
            else if (text.indexOf(WIDGET_COBROWSE_REQUEST_PREFIX) > -1) {
                // do not open a past co-browse request
                if (!isHistory) {
                    // auvious:cobrowse-request:<request-type>:<ticket>
                    log("Verint: cobrowse requested", text);
                    const request = text.split(":");
                    const requestType = request[2];
                    const ticket = request[3];
                    (_e = (_d = this.handlers).cobrowseRequested) === null || _e === void 0 ? void 0 : _e.call(_d, ticket, requestType);
                }
            }
            else {
                (_g = (_f = this.handlers).messageReceived) === null || _g === void 0 ? void 0 : _g.call(_f, this.archiveMessage({
                    id,
                    text,
                    type: "text",
                    isLocal,
                    timestamp,
                    status,
                    senderId,
                }));
            }
        }
        catch (ex) {
            log("Failed to handle message:", ex);
        }
    }
    /** interface implementations ***/
    createChat(contact, conversationType) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
        return __awaiter(this, void 0, void 0, function* () {
            // start guest session
            (_b = (_a = this.handlers) === null || _a === void 0 ? void 0 : _a.starting) === null || _b === void 0 ? void 0 : _b.call(_a);
            (_d = (_c = this.handlers) === null || _c === void 0 ? void 0 : _c.connecting) === null || _d === void 0 ? void 0 : _d.call(_c);
            yield this.initClient({
                email: contact.email,
                firstMessage: contact.subject,
                firstName: contact.firstName,
                lastName: contact.lastName,
            });
            this.setupArchiveOnRefresh();
            (_f = (_e = this.handlers) === null || _e === void 0 ? void 0 : _e.started) === null || _f === void 0 ? void 0 : _f.call(_e, {
                id: this.session.userId,
                jwt: null,
                memberId: this.session.userLoginId,
                uri: null,
            });
            yield ((_h = (_g = this.handlers) === null || _g === void 0 ? void 0 : _g.connected) === null || _h === void 0 ? void 0 : _h.call(_g));
            yield ((_k = (_j = this.handlers) === null || _j === void 0 ? void 0 : _j.agentNotified) === null || _k === void 0 ? void 0 : _k.call(_j));
            // join
            this.sessionContact = contact;
            this.sessionConversationType = conversationType;
            this.pollTask();
            while (yield this.getQueueStatus()) {
                yield new Promise((resolve) => setTimeout(resolve, 5000));
            }
        });
    }
    downloadFile(file) {
        return __awaiter(this, void 0, void 0, function* () {
            // could either be an uuid or a "[date].[filename]" string
            const attachment = this.attachments[file.id] ||
                this.attachments[file.id.slice(0, file.id.indexOf("."))];
            if (!(attachment === null || attachment === void 0 ? void 0 : attachment.url)) {
                throw codeError(new Error("file not found in attachments list"), ErrorCode.FILE_TRANSFER_API_ERROR);
            }
            try {
                const response = yield fetch(new URL(attachment.url, this.verintUrl), {
                    headers: {
                        authorization: `EM_session ${attachment.sessionId}`,
                    },
                });
                if (!response.ok) {
                    throw new Error(response.status + "");
                }
                const blob = yield response.blob();
                const blobUrl = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = blobUrl;
                link.download = file.name;
                link.hidden = true;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(blobUrl);
            }
            catch (e) {
                log("Error downloading file", e);
                throw codeError(e, ErrorCode.FILE_TRANSFER_API_ERROR);
            }
        });
    }
    sendFile(inputFileRef) {
        var _a, _b, _c, _d;
        return __awaiter(this, void 0, void 0, function* () {
            const file = inputFileRef.files[0];
            if (!file)
                return;
            const messageId = `${Date.now()}.${file.name}`;
            const messageSent = {
                id: messageId,
                type: "file",
                file: {
                    id: messageId,
                    name: file.name,
                    size: file.size,
                },
                isLocal: true,
                timestamp: new Date(),
                status: "sent",
                senderId: this.session.userId,
            };
            this.pendingMessages.push(messageSent);
            try {
                (_b = (_a = this.handlers).messageSent) === null || _b === void 0 ? void 0 : _b.call(_a, this.archiveMessage(messageSent));
                const formData = new FormData();
                formData.append("file", file, messageId);
                const response = yield ky.post(`${this.verintUrl}/live-chat-protocol-converter/${this.tenantId}/CONAV/chat/rest/api/clients/${this.session.userId}/attachments`, {
                    body: formData,
                });
                yield response.json();
            }
            catch (e) {
                log("Error uploading files", e);
                (_d = (_c = this.handlers).messageError) === null || _d === void 0 ? void 0 : _d.call(_c, this.archiveMessage(Object.assign(Object.assign({}, messageSent), { status: "error" })));
                throw codeError(e, ErrorCode.FILE_TRANSFER_API_ERROR);
            }
        });
    }
    /** in synchronous chat mode we have to keep track of message history */
    archiveMessage(msg) {
        if (!this.isAsyncChat) {
            const index = this.messageHistory.findIndex((m) => m.timestamp === msg.timestamp);
            if (index >= 0)
                this.messageHistory[index] = msg;
            else
                this.messageHistory.push(msg);
            // keep it sorted
            for (let i = this.messageHistory.length - 1; i > 0; i--) {
                if (this.messageHistory[i].timestamp.getTime() <
                    this.messageHistory[i - 1].timestamp.getTime()) {
                    const swap = this.messageHistory[i - 1].timestamp;
                    this.messageHistory[i - 1].timestamp =
                        this.messageHistory[i].timestamp;
                    this.messageHistory[i].timestamp = swap;
                }
                else {
                    break;
                }
            }
        }
        return msg;
    }
    pollTask() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.doPolling)
                return;
            this.doPolling = true;
            while (this.doPolling) {
                yield this.getEvents();
                yield timeout(1500);
            }
        });
    }
    endChat() {
        var _a, _b, _c;
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.session) {
                return;
            }
            yield ky.post(`${this.verintUrl}/live-chat-protocol-converter/${this.tenantId}/CONAV/chat/rest/api/clients/${this.session.userId}/logout`);
            (_a = this.handlers) === null || _a === void 0 ? void 0 : _a.customerLeft();
            (_b = this.handlers) === null || _b === void 0 ? void 0 : _b.agentLeft();
            (_c = this.handlers) === null || _c === void 0 ? void 0 : _c.ended();
            this.destroy();
        });
    }
    destroy() {
        this.session = null;
        this.doPolling = false;
        this.pendingMessages = [];
        this.attachments = {};
        this.$members = {};
        this.agentHasEntered = false;
        if (this.agentTypingTimeout !== -1) {
            clearTimeout(this.agentTypingTimeout);
            this.agentTypingTimeout = -1;
        }
        localStore.removeItem(KEY_VERINT_LIVE);
        localStore.removeItem(KEY_VERINT_LOG_MSG);
        localStore.removeItem(KEY_VERINT_LOG_ATTACH);
        localStore.removeItem(KEY_VERINT_LOG_MEMBERS);
    }
    sendTyping() {
        return __awaiter(this, void 0, void 0, function* () {
            const req = yield ky
                .post(`${this.verintUrl}/live-chat-protocol-converter/${this.tenantId}/CONAV/chat/rest/api/clients/${this.session.userId}/events`, {
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify([
                    {
                        type: "UserTyping",
                    },
                ]),
            })
                .json();
            this.handleEvents(req.events);
        });
    }
    acknowledgeMessages() {
        return __awaiter(this, void 0, void 0, function* () { });
    }
    sendMessage(message, bodyType, metadata) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            const msg = {
                id: Date.now() + "",
                text: message,
                type: "text",
                isLocal: true,
                timestamp: new Date(),
                status: "sent",
                senderId: this.session.userLoginId,
            };
            this.pendingMessages.push(msg);
            (_a = this.handlers) === null || _a === void 0 ? void 0 : _a.messageSent(this.archiveMessage(msg));
            try {
                const req = yield ky
                    .post(`${this.verintUrl}/live-chat-protocol-converter/${this.tenantId}/CONAV/chat/rest/api/clients/${this.session.userId}/events`, {
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify([
                        {
                            type: "MessageSent",
                            text: message,
                        },
                    ]),
                })
                    .json();
                this.handleEvents(req.events);
            }
            catch (_c) {
                (_b = this.handlers) === null || _b === void 0 ? void 0 : _b.messageError(this.archiveMessage(Object.assign(Object.assign({}, msg), { status: "error" })));
            }
        });
    }
    customerConnected() {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            return !!((_a = this.session) === null || _a === void 0 ? void 0 : _a.userId);
        });
    }
}
//# sourceMappingURL=verint.messaging.service.js.map